import * as Scrivito from "scrivito";
import moment from "moment";
import setLanguage from "^/utils/setLanguage";
import PageIsGoneException from "^/utils/PageIsGoneException";
import hasExpired from "^/utils/hasExpired";
import I18n from "^/config/i18n";
import ImageOrVideo from "^/Components/ImageOrVideo";
import { formatDateRange } from "^/utils/formatDateRange";

import { jsonLd } from "^/Widgets/SchemaWidget/Schemas";
import { SchemaWidgetComponent } from "^/Widgets/SchemaWidget/SchemaWidgetComponent";
import { EventPageLocation } from "./EventPageLocation";
import { EventList } from "./EventList";

Scrivito.provideComponent(
  "EventPage",
  Scrivito.connect(({ page }) => {
    setLanguage(page);
    const date = page.get("date");
    const endDate = page.get("endDate");
    const json = jsonLd.Event(page, page);

    if (!Scrivito.isInPlaceEditingActive() && hasExpired(endDate)) {
      throw new PageIsGoneException();
    }

    const language = page.language();
    const siteId = page.siteId();
    const eventsPage = Scrivito.Obj.onSite(siteId)
      .where("_objClass", "equals", "EventOverviewPage")
      .first();
    const currentHour = moment().startOf("day").toDate();
    const recommended = (eventsPage.get("recommendedEvents") || []).filter(
      (rec) => rec.get("endDate") >= currentHour || rec.get("endDate") === null
    );

    const upcoming = Scrivito.Obj.onSite(siteId)
      .all()
      .and("_objClass", "equals", "EventPage")
      .and("date", "isGreaterThan", moment().startOf("day").toDate())
      .order("date", "asc")
      .take(3);

    const hasMedia = page.get("image");
    const mediaClass = hasMedia ? "col-12 col-lg-5" : "";
    const contentClass = hasMedia ? "col-12 col-lg-7" : "col-12";

    return (
      <>
        <div className="page-header-widget bg-blue">
          <div className="container">
            <div className="row">
              {hasMedia && (
                <div className={`${mediaClass} position-relative`}>
                  <div className="page-header-widget__media">
                    <ImageOrVideo
                      widget={page}
                      className="page-header-widget__image img-fluid"
                    />
                  </div>
                </div>
              )}
              <div className={contentClass}>
                {page.get("shortTitle") ? (
                  <Scrivito.ContentTag
                    tag="h1"
                    className="page-header-widget__headline"
                    content={page}
                    attribute="shortTitle"
                  />
                ) : (
                  <Scrivito.ContentTag
                    tag="h1"
                    className="page-header-widget__headline"
                    content={page}
                    attribute="title"
                  />
                )}
                <Scrivito.ContentTag
                  className="page-header-widget__description"
                  content={page}
                  attribute="description"
                />
                <div className="mb-2">
                  {formatDateRange(date, endDate, {
                    locale: language || "de",
                    allDay: page.get("allDay"),
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="container pt-0">
          <nav
            aria-label="breadcrumb row"
            className="breadcrumb-wrapper --page-mode"
          />
        </div>

        <div className="container s-py-5">
          <div className="row">
            <div className="col-12 col-lg-8 col-xl-9 position-relative">
              <Scrivito.ContentTag
                content={page}
                attribute="title"
                tag="h2"
                className="m-0"
                role="heading"
                aria-level="2"
              />

              <EventPageLocation page={page} />
              <Scrivito.ContentTag
                tag="div"
                content={page}
                attribute="body"
                role="main"
              />
              <SchemaWidgetComponent type="Event" json={json} />
            </div>
            <div className="col-12 col-lg-4 col-xl-3">
              <h3 className="event-header">{eventsPage.get("title")}</h3>
              <Scrivito.ContentTag
                content={page}
                attribute="add"
                tag="div"
                className="m-0"
              />
              <Scrivito.LinkTag to={eventsPage} className="btn bg-blue">
                {I18n.t("Event all events")}
              </Scrivito.LinkTag>
              <EventList
                list={recommended}
                headline={I18n.t("Event recommended for you")}
              />
              <EventList list={upcoming} headline={I18n.t("Event upcoming")} />
            </div>
          </div>
        </div>
      </>
    );
  })
);
